import { z } from 'zod/v3';
import { toast } from '@cotera/client/app/components/ui/toaster';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  ExecuteContext,
  BaseAction,
} from '../../actions/framework';
import { Assert } from '@cotera/utilities';
import { DatasetModel } from '../model/dataset.model';
import { DATASET_ACTION_GROUP } from './constants';
import { AskResponse } from '../../actions/framework/types';
import {
  FilterBuilder,
  filterGroupToExpression,
  ModalPageView,
  useOptions,
} from '@cotera/client/app/components/app';
import { GraphModel } from '../../graph/graph.model';
import { CreateChildAction } from './create-child.action';
import { DG } from '@cotera/sdk/core';
import { useState } from 'react';
import { FilterGroup } from '@cotera/client/app/components/app/filter-builder/types';
import { Button } from '../../components/ui/button';

type Params = {
  where: string;
};

export class CreateViewAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Create View';
  description = 'Create a view of the current dataset';
  inputSchema = z.object({
    where: z.string(),
  });
  group = DATASET_ACTION_GROUP;
  icon = 'filter';

  constructor(
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      pop: () => void;
    }
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    const model = context.targetedModelOfType(DatasetModel);
    return model !== null;
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(DatasetModel)!;
    const graph = context.getInScopeModelOfType(GraphModel);

    Assert.assert(model !== null, 'Dataset not found');
    Assert.assert(graph !== null, 'Graph not found');

    const datasetName = model.currentNameInGraph.snapshot();
    Assert.assert(datasetName !== null, 'Dataset name not found');

    const saveChildAction = context.getAction(CreateChildAction);

    const parser = graph.graph.snapshot().parser;

    const initialEraQl = `${DG.Graph.symbolEraQL(datasetName)} |> Where(${
      payload.where
    })`;

    const relResult = parser.parseRel(initialEraQl);

    if (relResult.isErr()) {
      toast.error(JSON.stringify(relResult.error));

      return err([
        {
          t: 'FailedToSaveView',
          message: 'Failed to save view',
        },
      ]);
    }

    const result = await saveChildAction.askOrExecute(
      {
        initialEraQl,
      },
      context
    );

    if (result.isErr()) {
      toast.error(JSON.stringify(result.error));
      return err([
        {
          t: 'FailedToSaveView',
          message: 'Failed to save view',
        },
      ]);
    }

    return ok({});
  }

  shouldAsk(_: ApplicableContext): boolean {
    return true;
  }

  ask(_: {}, context: ExecuteContext): AskResponse<Params> {
    const model = context.targetedModelOfType(DatasetModel)!;

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => {
            return (
              <FilterHaveChangedAskView
                onRespond={async (params) => {
                  await onRespond({
                    where: params.where,
                  });
                  this.modalManager.pop();
                }}
                onCancel={async () => {
                  this.modalManager.pop();
                  await onCancel();
                }}
                model={model}
              />
            );
          },
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

const FilterHaveChangedAskView: React.FC<{
  onRespond: (params: Params) => Promise<void> | void;
  onCancel: () => Promise<void> | void;
  model: DatasetModel;
}> = ({ onRespond, onCancel, model }) => {
  const item = model.item.snapshot();
  Assert.assert(item !== null, 'Item not found');
  const rel = item.rel;

  const options = useOptions(rel.attributes);
  const [filterGroup, setFilterGroup] = useState<FilterGroup>(
    model.filterGroup.snapshot()
  );

  return (
    <ModalPageView
      title={`Save Child Dataset`}
      subtitle={`Optionally specify filters to apply to the child dataset`}
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <FilterBuilder
          dangerouslyOptInToDynamicOptions
          filterGroup={filterGroup}
          options={options}
          autoRun={false}
          showRun={false}
          rel={rel}
          runEnabled
          runVariants={[
            {
              label: 'Refresh Data',
              id: 'source',
              icon: 'filter',
            },
          ]}
          onChange={({ filterGroup }) => {
            setFilterGroup(filterGroup);
          }}
          onRun={() => {
            //run is handled by the dataset model
          }}
        />
        <div className="w-full flex justify-end gap-2">
          <Button
            type="submit"
            variant="outline"
            className="w-fit self-end"
            onClick={async () => {
              await onCancel();
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            className="w-fit self-end"
            onClick={async () => {
              await onRespond({
                where: filterGroupToExpression(rel.ref('from'))(
                  filterGroup
                ).toEraQL(),
              });
            }}
          >
            Create Child
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
